import { useState } from 'react'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  TabsHeader,
  TabsHeaderActions,
  TabsHeaderTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui'
import { ChevronDown, Copy, Eye, Plus } from 'lucide-react'
import { cn } from '../../lib/utils'
import { SkeletonBoxes } from '../../components/skeleton/Skeleton'
import { BPSBadge, GroupStatusBadge } from '../../components/badges/status'
import { ellipsise } from '../../utils/strings'
import { ConfirmationDialog } from '../../components/dialogs/ConfirmationDialog'

export interface GroupData {
  groupId: string
  name: string
  status: string
  imageUrl?: string
  admin: string[]
  companies: number
  usdValueOfAvailableBalance: string
  groupBps?: number
  liquidityProviderBps?: number
}

export interface GroupsTabViewProps {
  groups: GroupData[]
  loading?: boolean
  searchTerm?: string
  title?: string
  showCreateButton?: boolean
  showAssignButton?: boolean
  availableGroups?: GroupData[]
  userId?: string
  onViewGroup?: (groupId: string) => void
  onCreateGroup?: () => void
  onAssignToGroup?: (groupId: string, groupName: string) => Promise<void>
  onUnassignFromGroup?: (groupId: string, groupName: string) => Promise<void>
  onCopyId?: (groupId: string) => void
  assigning?: boolean
  removing?: boolean
}

export function GroupsTabView({
  groups,
  loading = false,
  searchTerm = '',
  title = 'Groups',
  showCreateButton = false,
  showAssignButton = false,
  availableGroups = [],
  userId,
  onViewGroup,
  onCreateGroup,
  onAssignToGroup,
  onUnassignFromGroup,
  onCopyId,
  assigning = false,
  removing = false,
}: GroupsTabViewProps) {
  const [confirmUnassignOpen, setConfirmUnassignOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<{ groupId: string; name: string } | null>(null)

  const filteredGroups = groups
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((group) => {
      if (!searchTerm) return true
      return (
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.admin.some((admin) => admin.toLowerCase().includes(searchTerm.toLowerCase())) ||
        group.groupId.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })

  const handleUnassignClick = (group: { groupId: string; name: string }) => {
    setSelectedGroup(group)
    setConfirmUnassignOpen(true)
  }

  const handleUnassignConfirm = async () => {
    if (!selectedGroup || !onUnassignFromGroup) return

    try {
      await onUnassignFromGroup(selectedGroup.groupId, selectedGroup.name)
      setConfirmUnassignOpen(false)
      setSelectedGroup(null)
    } catch (error) {
      console.error('Error unassigning from group:', error)
    }
  }

  const handleAssignToGroup = async (groupId: string, groupName: string) => {
    if (!onAssignToGroup) return

    try {
      await onAssignToGroup(groupId, groupName)
    } catch (error) {
      console.error('Error assigning to group:', error)
    }
  }

  const handleCopyId = (groupId: string) => {
    if (onCopyId) {
      onCopyId(groupId)
    } else {
      navigator.clipboard.writeText(groupId)
    }
  }

  const Title = ({ children, className }: { children: React.ReactNode; className?: string }) => {
    return (
      <div className={cn('text-xs text-card-text rounded-md w-fit', className)}>{children}</div>
    )
  }

  return (
    <div className="space-y-4">
      <TabsHeader className="flex-row justify-between">
        <TabsHeaderTitle>{title}</TabsHeaderTitle>
        <TabsHeaderActions>
          {showCreateButton && (
            <Button variant="main_action" onClick={onCreateGroup}>
              <Plus className="h-3 md:h-4 w-3 md:w-4" />
              <span>Create</span>
            </Button>
          )}
          {showAssignButton && availableGroups.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="main_outline" disabled={assigning}>
                  {assigning ? 'Assigning...' : 'Assign to Group'}
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                {availableGroups.map((group) => (
                  <DropdownMenuItem
                    key={group.groupId}
                    onClick={() => handleAssignToGroup(group.groupId, group.name)}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{group.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {group.companies} companies
                      </span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TabsHeaderActions>
      </TabsHeader>

      {loading ? (
        <SkeletonBoxes />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredGroups.map((group) => (
            <Card key={group.groupId} className="flex flex-col h-full">
              <CardHeader>
                <div className="flex items-center gap-2 overflow-hidden">
                  {group.imageUrl ? (
                    <img src={group.imageUrl} alt="Group" className="w-10 h-10 rounded-lg" />
                  ) : (
                    <div className="w-10 h-10 shrink-0 rounded-lg bg-gray-200" />
                  )}
                  <div className="flex items-center gap-1 justify-between flex-wrap overflow-hidden w-full">
                    <CardTitle className="text-2xl truncate mr-1">{group.name}</CardTitle>
                    <div className="flex items-center gap-1 shrink-0">
                      {group.groupBps != null && group.liquidityProviderBps != null && (
                        <>
                          <BPSBadge bps={group.groupBps} tone="group" />
                          <BPSBadge bps={group.liquidityProviderBps} tone="lp" />
                        </>
                      )}
                      <GroupStatusBadge status={group.status} />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 flex-1">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Title>
                      <span>Group Owners</span>
                    </Title>
                    <div className="flex flex-col gap-2">
                      {group.admin.length > 0 ? (
                        <>
                          {group.admin.slice(0, 2).map((admin, index) => (
                            <div key={index} className="font-medium text-sm">
                              {ellipsise(admin, 20)}
                            </div>
                          ))}
                          {group.admin.length > 2 && (
                            <div className="font-medium text-sm text-gray-500">
                              {group.admin.length - 2} more
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="font-medium text-sm">No admins</div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-4 pl-3">
                    <div className="space-y-2">
                      <Title>
                        <span>Available Balance</span>
                      </Title>
                      <div className="font-medium text-sm">{group.usdValueOfAvailableBalance}</div>
                    </div>
                    <div className="space-y-2">
                      <Title>
                        <span>Active Companies</span>
                      </Title>
                      <div className="font-medium text-sm">{group.companies}</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Title>
                    <span>ID</span>
                  </Title>
                  <div className="flex items-center gap-2">
                    <div className="text-sm">{ellipsise(group.groupId, 10)}</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'text-xs text-card-text border border-card-border rounded-md px-2 py-1 w-fit cursor-pointer',
                        'h-auto md:h-auto rounded-md px-2 py-1.5 has-[>svg]:px-2.5',
                      )}
                      onClick={() => handleCopyId(group.groupId)}
                    >
                      <Copy className="size-3" />
                      <span className="text-xs leading-none">Copy ID</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <div className="flex gap-2 w-full">
                  {onViewGroup && (
                    <Button
                      variant="main_action"
                      className="flex-1 rounded-md"
                      onClick={() => onViewGroup(group.groupId)}
                    >
                      <Eye className="w-2 h-2" />
                      View
                    </Button>
                  )}
                  {userId && onUnassignFromGroup && (
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleUnassignClick(group)}
                      disabled={removing}
                    >
                      Unassign
                    </Button>
                  )}
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {onUnassignFromGroup && (
        <ConfirmationDialog
          open={confirmUnassignOpen}
          onOpenChange={setConfirmUnassignOpen}
          title="Confirm Unassignment"
          description={`Are you sure you want to unassign this user from the group "${selectedGroup?.name}"? This action cannot be undone and will remove the user's access to this group.`}
          confirmText="Unassign"
          cancelText="Cancel"
          onConfirm={handleUnassignConfirm}
          isLoading={removing}
          variant="destructive"
        />
      )}
    </div>
  )
}
